/**
 * The dashboard's words and timings (`features/dashboard/dashboard.json`, Zod-validated; spec §5.8–5.10). Every
 * label the shell shows comes from here, so the naming pass is a data edit.
 */
import { z } from 'zod'
import configJson from '../../features/dashboard/dashboard.json'
import { DASH_TABS } from './registry'

const text = z.string().min(1)
const tab = z.strictObject({ label: text, short: text })
const linkTarget = z.strictObject({ tab: z.enum(DASH_TABS), section: text })

export const dashboardConfigSchema = z.strictObject({
  comment: z.string(),
  refreshHz: z.number().positive().max(60),
  badgeHz: z.number().positive().max(4),
  deepLinks: z.boolean(),
  /** Where each deep-link action lands (spec §1.2); the section falls back to the tab until W3 registers it. */
  deepLinkTargets: z.strictObject({ wirePanel: linkTarget, charterBoard: linkTarget, fleetScreen: linkTarget, logs: linkTarget }),
  /** Rows a PgUp/PgDn moves the focus in a section list (spec §1.6). */
  focusPageRows: z.number().int().positive(),
  openKey: text,
  title: text,
  gameTitle: text,
  tabs: z.strictObject({
    overview: tab,
    ship: tab,
    jobs: tab,
    fleet: tab,
    nav: tab,
    intel: tab,
    ledger: tab,
    log: tab,
  }),
  badges: z.strictObject({ paused: text, live: text }),
  close: text,
  closeKey: text,
  comingSoon: text,
  /** Per content tab: what is coming and, where one exists, the key that opens it today (W2 gate: no route back here). */
  comingSoonWhy: z.strictObject({ ship: text, jobs: text, fleet: text, nav: text, intel: text, ledger: text, log: text }),
  unavailable: text,
  hints: z.strictObject({ tab: text, jump: text, section: text, open: text, back: text, close: text, menu: text }),
  /** DockedAction words (C0 adds the where line and the reason per station service). */
  dockedAction: z.strictObject({
    nearest: text,
    none: text,
    setCourse: text,
    thisSystem: text,
    here: text,
    jump: text,
    jumps: text,
    reasons: z.strictObject({
      market: text,
      repair: text,
      outfitting: text,
      contracts: text,
      'chart-buyer': text,
      'parts-vendor': text,
      'lease-office': text,
    }),
  }),
  age: z.strictObject({
    lastSeen: text,
    never: text,
    here: text,
    freshDays: z.number().positive(),
    staleDays: z.number().positive(),
  }),
})

export type DashboardConfig = z.infer<typeof dashboardConfigSchema>

let cached: DashboardConfig | null = null

export function loadDashboardConfig(): DashboardConfig {
  if (cached !== null) return cached
  const c = dashboardConfigSchema.parse(configJson)
  if (Object.keys(c.tabs).join() !== DASH_TABS.join()) throw new Error('dashboard.json: tabs must list the 8 fixed tabs in order')
  if (c.age.staleDays < c.age.freshDays) throw new Error('dashboard.json: age.staleDays must be ≥ freshDays')
  cached = c
  return c
}
